import { Link } from '@tanstack/react-router';

import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { genNameInitials } from '@/utils/name';

interface Person {
  id?: string;
  name?: string;
  avatarUrl?: string;
  email?: string;
}

interface Props {
  person: Person | null;
  variant?: 'unknown' | 'noPerson';
  alumni?: boolean;
  /**
   * true のとき氏名をリンクにせず、親（例: テーブル行）のクリックに委ねる。
   * 行クリックで詳細へ遷移する一覧でプロフィール Link と競合しないようにする。
   */
  disableProfileLink?: boolean;
  /**
   * デフォルトではプロファイルにリンクするが、その他のページにリンクしたい場合に指定。
   * $id を含めると personId に置き換わります。
   * @example "/person/$id"
   */
  linkTo?: string;
  openNewTab?: boolean;
}

/**
 * PersonCellのvariantオプション
 *
 * 'unknown': メンバーが存在するはずだが特定できない場合(メンバーの存在が必須)
 * - 例: シャドーIT検知結果に利用者のemailは存在するが、対応するメンバー(person)がDressCode上に存在しない
 *
 * 'noPerson': 明確にメンバーと紐付いていない、または紐づき自体が任意の場合
 * - 例: デバイス一覧での未割り当てデバイス
 * - 例: 在籍・離籍者一覧でのappUser(メンバーに紐付くAppUserは任意のため)
 */
const PersonCell = ({
  person,
  variant = 'noPerson',
  alumni,
  disableProfileLink = false,
  linkTo,
  openNewTab = false,
}: Props) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.dataTable.cells.PersonCell',
  });

  if (!person || (!person.id && !person.name)) {
    return (
      <HStack className="items-center gap-3">
        <VStack className="gap-2">
          <Icon
            iconName={
              variant === 'unknown' ? 'UserRoundXIcon' : 'UserRoundSearchIcon'
            }
            text={t(variant === 'unknown' ? 'unknownPerson' : 'noPerson')}
            variant="small"
            colorVariant="error"
            textColor="text-error-on-neutral-fill"
          />
          {variant === 'unknown' && person?.email && (
            <div className="text-xs text-neutral-muted-on-fill">
              {person.email}
            </div>
          )}
        </VStack>
      </HStack>
    );
  }

  return (
    <HStack className="items-center gap-3">
      <Avatar>
        <AvatarImage src={person.avatarUrl} alt={person.name || ''} />
        <AvatarFallback>
          {person.name ? genNameInitials(person.name) : ''}
        </AvatarFallback>
      </Avatar>
      <VStack className="font-semibold">
        {person.id && !disableProfileLink ? (
          <Link
            to={linkTo ?? (alumni ? '/person/alumni/$id' : '/person/$id')}
            params={{ id: person.id }}
            className="text-sm hover:underline"
            target={openNewTab ? '_blank' : undefined}
          >
            {person.name}
          </Link>
        ) : (
          <div className="text-sm">{person.name}</div>
        )}
        {person.email && (
          <div className="text-xs text-neutral-muted-on-fill">
            {person.email}
          </div>
        )}
      </VStack>
    </HStack>
  );
};

export default PersonCell;
